// Promos de venta del equipo en el detalle de «Elige el teléfono» (slot `detailPromotions`, REQ-09),
// en los grupos «Regalo», «Descuento», «Beneficio» y «Cupón». Grupo vacío no se pinta.
import { useMemo } from 'react';
import { Gift, Tag, Ticket, Wallet } from 'lucide-react';
import { useCatalog } from '@/domains/sales/wizard/api/useWizard';
import {
    PromotionGroup,
    PromotionGroupRow,
} from '@/domains/sales/wizard/components/PromotionGroup';
import type { RenderAccountBonuses } from '@/domains/sales/wizard/promotionSlots';
import { itemDisplayName } from '@/shared/lib/format/itemDisplayName';
import { formatMXN } from '@/shared/lib/format/money';
import { useSkuSalePromotions } from '../api/useWizardPromotions';
import type { StoreSalePromotionItem } from '../api/wizardPromotionsContract';
import {
    saleBonusReach,
    splitByBenefit,
    type BenefitGroup,
    type GiftDetail,
    type PromotionPiece,
} from '../lib/skuPromotionPieces';
import { giftValueCentsOf } from '../lib/giftPanel';
import { isSureKeepOnly } from '../lib/promotionAppliesTo';
import { afterSaleText } from './AfterSaleBenefits';
import { PaymentOptionsChip } from './PaymentOptionsChip';

const GROUPS: readonly { group: BenefitGroup; label: string; icon: typeof Gift }[] = [
    { group: 'gift', label: 'Regalo', icon: Gift },
    { group: 'discount', label: 'Descuento', icon: Tag },
    { group: 'benefit', label: 'Beneficio', icon: Wallet },
    { group: 'coupon', label: 'Cupón', icon: Ticket },
];

/** Separador antes de la opción `index`: «o» si se elige, «y» si se lleva todas; comas en medio. */
function separatorBefore(index: number, total: number, word: string, allWords: boolean): string {
    if (index === 0) return '';
    return allWords || index === total - 1 ? ` ${word} ` : ', ';
}

/** «Elige 1: Audífonos (valor $399.00) o Cargador (sin existencia)», en una línea. */
export function giftOptionsText(gift: GiftDetail, names: ReadonlyMap<string, string>): string {
    const options = gift.options
        .map((option) => ({ ...option, displayName: names.get(option.sku) ?? option.name }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName, 'es-MX'));
    if (options.length === 0) return '';
    const chooses = gift.chooseCount < options.length;
    const one = gift.chooseCount === 1;
    const prefix = !chooses ? '' : one ? 'Elige 1: ' : `Elige ${String(gift.chooseCount)} de: `;
    const word = chooses ? 'o' : 'y';
    return (
        prefix +
        options
            .map(
                (option, index) =>
                    separatorBefore(index, options.length, word, chooses && one) +
                    `${option.displayName} ${
                        option.inStock
                            ? `(valor ${formatMXN(giftValueCentsOf(option) / 100)})`
                            : '(sin existencia)'
                    }`,
            )
            .join('')
    );
}

function PieceRow({
    piece,
    icon,
    names,
    reach,
}: {
    piece: PromotionPiece;
    icon: typeof Gift;
    names: ReadonlyMap<string, string>;
    reach: string | null;
}) {
    const name = piece.gift?.includedInSale ? `Incluido en ${piece.name}` : piece.name;
    return (
        <PromotionGroupRow
            icon={icon}
            tone={piece.group}
            name={name}
            aside={piece.paymentLabel && <PaymentOptionsChip label={piece.paymentLabel} />}
            description={[
                ...(piece.gift ? [giftOptionsText(piece.gift, names)] : []),
                ...piece.texts.map((text, index) =>
                    piece.walletBonus && index === 0 && reach !== null
                        ? `${text} · ${reach}`
                        : text,
                ),
            ]
                .filter((text) => text !== '')
                .join(' · ')}
            meta={[piece.extras, piece.conditions].filter(Boolean).join(' · ')}
            metaTone={piece.extras !== null ? 'group' : 'muted'}
        />
    );
}

/** Nombre de catálogo por SKU, tomado del catálogo del wizard ya cargado. */
function useCatalogNames(sessionId: string): ReadonlyMap<string, string> {
    const { data } = useCatalog(sessionId, { cached: true });
    return useMemo(
        () => new Map((data?.items ?? []).map((item) => [item.sku, itemDisplayName(item)])),
        [data],
    );
}

export function SkuPromotionsDetail({
    sessionId,
    sku,
    renderBonuses,
    storePromotions,
    withoutSureKeep = false,
    hideLabel = false,
}: {
    sessionId: string;
    sku: string;
    /** Lo que pinta el detalle mientras cargan las promos. */
    renderBonuses: RenderAccountBonuses;
    /** Promos de la tienda del banner; de ellas sale el alcance de cada bono. */
    storePromotions?: readonly StoreSalePromotionItem[] | undefined;
    /** SureKeep no financia el equipo: las promos solo SureKeep no se muestran. */
    withoutSureKeep?: boolean;
    /** Ocultar los headers REGALO/DESCUENTO/BENEFICIO/CUPÓN: el color de la caja basta. */
    hideLabel?: boolean;
}) {
    const { data } = useSkuSalePromotions(sessionId, sku);
    const names = useCatalogNames(sessionId);
    if (!data) return renderBonuses([]);
    const promotions = withoutSureKeep
        ? data.promotions.filter((p) => !isSureKeepOnly(p))
        : data.promotions;
    const pieces = splitByBenefit(promotions, afterSaleText);
    return (
        <>
            {GROUPS.map(({ group, label, icon }) => (
                <PromotionGroup
                    key={group}
                    label={label}
                    icon={icon}
                    tone={group}
                    hideLabel={hideLabel}
                    items={pieces[group].map((piece) => ({
                        key: piece.promotionId,
                        node: (
                            <PieceRow
                                piece={piece}
                                icon={icon}
                                names={names}
                                reach={saleBonusReach(piece.promotionId, storePromotions)}
                            />
                        ),
                    }))}
                />
            ))}
        </>
    );
}
